/**
 * Lógica pura de la pestaña Ponderación y de los multiplicadores por defecto (Configuración). Los cálculos en vivo
 * replican los del backend (`app/unidades/reglas.py`) con enteros (`BigInt`), nunca float:
 * - m² ponderados = cubierta × m1 + semicubierta × m2 + descubierta × m3 (al centésimo, half-even);
 * - m² homologados = ponderados × coef. altura del depto (al centésimo, half-even);
 * - % del fideicomiso = homologados ÷ Σ de las unidades no fuera de venta, al centésimo y cerrando en 100;
 * - precio sugerido = homologados × precio base USD por m², a USD 1.000 (la mitad para arriba).
 * Lo que vale es lo que devuelve el servidor al guardar; esto es la vista previa mientras se tipea.
 */
import type { Ponderacion } from "./api";
import { aCentavos, deCentavos, montoAInput, parseMonto } from "./gastos";
import { coeficienteAInput, etiquetaPiso, formatM2, nombreUnidad, parseCoeficiente } from "./unidades";

export const MULTIPLICADORES = ["coef_cubierta", "coef_semicubierta", "coef_descubierta"] as const;
export type CampoMultiplicador = (typeof MULTIPLICADORES)[number];
export type CampoDepto = CampoMultiplicador | "coef_altura";

const SUPERFICIES = ["sup_cubierta_m2", "sup_semicubierta_m2", "sup_descubierta_m2"] as const;

export interface FormDepto {
  id: number;
  nombre: string; // "1° A"
  piso: number;
  sup_cubierta_m2: string; // decimales del servidor ("50.00"): no se editan acá
  sup_semicubierta_m2: string;
  sup_descubierta_m2: string;
  fuera_de_venta: boolean;
  coef_cubierta: string; // inputs ("0,5")
  coef_semicubierta: string;
  coef_descubierta: string;
  coef_altura: string; // input ("0,92"): arranca en el estándar de su piso
  estandar: string; // el estándar de su piso, para mostrar ("0,92")
}

/** Cochera, baulera, local u oficina: un multiplicador sobre su superficie total (sin coef. altura). */
export interface FormOtra {
  id: number;
  nombre: string; // "Cochera SS1 12"
  m2: string; // superficie total del servidor ("12.00")
  fuera_de_venta: boolean;
  multiplicador: string; // input ("0,5")
}

export interface FormPestana {
  departamentos: FormDepto[];
  otras: FormOtra[];
  precio_base: string; // USD por m² homologado (input; vacío = sin precio sugerido)
}

export interface ErroresPestana {
  departamentos?: Record<number, Partial<Record<CampoDepto, string>>>;
  otras?: Record<number, string>;
  precio_base?: string;
}

export function formPestana(p: Ponderacion): FormPestana {
  return {
    departamentos: p.departamentos.map((d) => ({
      id: d.id,
      nombre: `${etiquetaPiso(d.piso)} ${d.numero}`,
      piso: d.piso,
      sup_cubierta_m2: d.sup_cubierta_m2,
      sup_semicubierta_m2: d.sup_semicubierta_m2,
      sup_descubierta_m2: d.sup_descubierta_m2,
      fuera_de_venta: d.fuera_de_venta,
      coef_cubierta: coeficienteAInput(d.coef_cubierta),
      coef_semicubierta: coeficienteAInput(d.coef_semicubierta),
      coef_descubierta: coeficienteAInput(d.coef_descubierta),
      coef_altura: coeficienteAInput(d.coef_altura),
      estandar: coeficienteAInput(d.coef_altura_estandar),
    })),
    otras: p.otras_unidades.map((o) => ({ id: o.id, nombre: nombreUnidad(o), m2: o.m2, fuera_de_venta: o.fuera_de_venta, multiplicador: coeficienteAInput(o.multiplicador) })),
    precio_base: p.precio_base_usd_m2 === null ? "" : montoAInput(p.precio_base_usd_m2),
  };
}

/** Precio base para la API, con dos decimales ("1800,5" → "1800.50"): vacío → null; inválido → undefined (error). */
function precioBase(texto: string): string | null | undefined {
  if (texto.trim() === "") return null;
  const monto = parseMonto(texto);
  return monto === null ? undefined : deCentavos(aCentavos(monto));
}

const positivo = (texto: string): string | null => {
  const c = parseCoeficiente(texto);
  return c === null || c === "0.000" ? null : c;
};

const MSJ_MULTIPLICADOR = "De 0 a 9,999";
const MSJ_POSITIVO = "Mayor a 0 y menor a 10";

export function validarPestana(f: FormPestana): ErroresPestana {
  const e: ErroresPestana = {};
  const deptos: Record<number, Partial<Record<CampoDepto, string>>> = {};
  for (const d of f.departamentos) {
    const err: Partial<Record<CampoDepto, string>> = {};
    for (const campo of MULTIPLICADORES) if (parseCoeficiente(d[campo]) === null) err[campo] = MSJ_MULTIPLICADOR;
    if (positivo(d.coef_altura) === null) err.coef_altura = MSJ_POSITIVO;
    if (Object.keys(err).length > 0) deptos[d.id] = err;
  }
  if (Object.keys(deptos).length > 0) e.departamentos = deptos;
  const otras: Record<number, string> = {};
  for (const o of f.otras) if (positivo(o.multiplicador) === null) otras[o.id] = MSJ_POSITIVO;
  if (Object.keys(otras).length > 0) e.otras = otras;
  if (precioBase(f.precio_base) === undefined) e.precio_base = "Un monto mayor a 0, hasta 2 decimales (o vacío)";
  return e;
}

export const hayErrores = (e: ErroresPestana): boolean =>
  e.departamentos !== undefined || e.otras !== undefined || e.precio_base !== undefined;

export function aPayloadPestana(f: FormPestana) {
  return {
    departamentos: f.departamentos.map((d) => ({
      id: d.id,
      coef_cubierta: parseCoeficiente(d.coef_cubierta) ?? "0.000",
      coef_semicubierta: parseCoeficiente(d.coef_semicubierta) ?? "0.000",
      coef_descubierta: parseCoeficiente(d.coef_descubierta) ?? "0.000",
      coef_altura: positivo(d.coef_altura) ?? parseCoeficiente(d.estandar) ?? "1.000",
    })),
    otras: f.otras.map((o) => ({ id: o.id, multiplicador: positivo(o.multiplicador) ?? "1.000" })),
    precio_base_usd_m2: precioBase(f.precio_base) ?? null,
  };
}

/** Hay algo para guardar: compara lo que se mandaría (así "0,5" y "0,50" son lo mismo). */
export function hayCambios(f: FormPestana, original: FormPestana): boolean {
  return JSON.stringify(aPayloadPestana(f)) !== JSON.stringify(aPayloadPestana(original));
}

// El paso 1 (multiplicadores → m² ponderados) se guarda aparte del resto (Mati, 2026-10-09).

/** `base` con los multiplicadores de cada depto y cochera tomados de `de` (el resto queda como en `base`). */
export function conMultiplicadores(base: FormPestana, de: FormPestana): FormPestana {
  const porId = new Map(de.departamentos.map((d) => [d.id, d]));
  const otrasPorId = new Map(de.otras.map((o) => [o.id, o]));
  return {
    ...base,
    otras: base.otras.map((o) => {
      const otra = otrasPorId.get(o.id);
      return otra ? { ...o, multiplicador: otra.multiplicador } : o;
    }),
    departamentos: base.departamentos.map((d) => {
      const otro = porId.get(d.id);
      return otro ? { ...d, coef_cubierta: otro.coef_cubierta, coef_semicubierta: otro.coef_semicubierta, coef_descubierta: otro.coef_descubierta } : d;
    }),
  };
}

// El paso 2 (coef. altura) también, aparte (Mati, 2026-10-10); el precio base, con su botón en el resumen.

/** `base` con el coef. altura de cada depto tomado de `de`. */
export function conAlturas(base: FormPestana, de: FormPestana): FormPestana {
  const porId = new Map(de.departamentos.map((d) => [d.id, d]));
  return { ...base, departamentos: base.departamentos.map((d) => ({ ...d, coef_altura: porId.get(d.id)?.coef_altura ?? d.coef_altura })) };
}

/** `base` con el precio base de `de`. */
export const conPrecio = (base: FormPestana, de: FormPestana): FormPestana => ({ ...base, precio_base: de.precio_base });

export const hayCambiosPaso1 = (f: FormPestana, original: FormPestana): boolean => hayCambios(conMultiplicadores(original, f), original);
export const hayCambiosPaso2 = (f: FormPestana, original: FormPestana): boolean => hayCambios(conAlturas(original, f), original);
export const hayCambiosPrecio = (f: FormPestana, original: FormPestana): boolean => hayCambios(conPrecio(original, f), original);

const algunDepto = (e: ErroresPestana, campos: readonly CampoDepto[]): boolean =>
  Object.values(e.departamentos ?? {}).some((err) => campos.some((c) => err[c] !== undefined));

export const hayErroresPaso1 = (e: ErroresPestana): boolean => e.otras !== undefined || algunDepto(e, MULTIPLICADORES);
export const hayErroresPaso2 = (e: ErroresPestana): boolean => algunDepto(e, ["coef_altura"]);

/** Lo que va arriba de cada multiplicador: ["50 m² cub.", "0 m² semi.", "12,5 m² desc."]. */
export function etiquetasSuperficie(d: FormDepto): [string, string, string] {
  return [`${formatM2(d.sup_cubierta_m2)} cub.`, `${formatM2(d.sup_semicubierta_m2)} semi.`, `${formatM2(d.sup_descubierta_m2)} desc.`];
}

// ------------------------------------------------------------------ cálculo en vivo (enteros)

/** "62.5" → 6250n (centésimos); "0.5" → 500n con `escala` 1000n (milésimos). */
const aEntero = (decimal: string, escala: bigint): bigint => {
  const digitos = String(escala).length - 1;
  const [ent = "0", dec = ""] = decimal.split(".");
  return BigInt(ent) * escala + BigInt((dec + "0".repeat(digitos)).slice(0, digitos) || "0");
};

/** n ÷ d redondeado half-even (n ≥ 0, d > 0). */
const dividirParAlMedio = (n: bigint, d: bigint): bigint => {
  const q = n / d;
  const r2 = (n % d) * 2n;
  if (r2 > d || (r2 === d && q % 2n === 1n)) return q + 1n;
  return q;
};

const deCentesimos = (c: bigint): string => `${c / 100n}.${String(c % 100n).padStart(2, "0")}`;

/** m² ponderados del depto en centésimos; null si algún multiplicador es inválido. */
function ponderadosC(d: FormDepto): bigint | null {
  let suma = 0n;
  for (const [i, campo] of MULTIPLICADORES.entries()) {
    const c = parseCoeficiente(d[campo]);
    if (c === null) return null;
    suma += aEntero(d[SUPERFICIES[i]!], 100n) * aEntero(c, 1000n); // escala 1e-5
  }
  return dividirParAlMedio(suma, 1000n);
}

function homologadosC(ponderados: bigint, altura: string | null): bigint | null {
  if (altura === null) return null;
  return dividirParAlMedio(ponderados * aEntero(altura, 1000n), 1000n);
}

/** Como `reglas.precio_sugerido`: m² homologados (centésimos) × precio base, a USD 1.000 half-up; 0 → null. */
export function precioSugerido(homologados: bigint, precioBase: string | null): string | null {
  if (precioBase === null) return null;
  const escala = 100n * 100n * 1000n; // centésimos de m² × centavos → miles de USD
  const miles = (homologados * aCentavos(precioBase) * 2n + escala) / (2n * escala);
  return miles <= 0n ? null : `${miles * 1000n}.00`;
}

/** Como `reglas.repartir_porcentajes`: % al centésimo de cada valor (en centésimos de punto), cerrando en 100. */
export function repartirPorcentajes(valores: bigint[]): bigint[] {
  const total = valores.reduce((a, v) => a + v, 0n);
  if (total <= 0n) return valores.map(() => 0n);
  const nums = valores.map((v) => v * 10000n);
  const redondos = nums.map((n) => (n * 2n + total) / (2n * total)); // half-up
  const diferencia = 10000n - redondos.reduce((a, r) => a + r, 0n);
  const paso = diferencia > 0n ? 1n : -1n;
  const resto = (i: number) => nums[i]! - redondos[i]! * total; // proporcional a exacto − redondo
  const orden = valores.map((_, i) => i).sort((a, b) => {
    const ra = -paso * resto(a);
    const rb = -paso * resto(b);
    return ra < rb ? -1 : ra > rb ? 1 : a - b;
  });
  const cuantos = Number(diferencia < 0n ? -diferencia : diferencia);
  for (const i of orden.slice(0, cuantos)) redondos[i]! += paso;
  return redondos;
}

export interface CalculoDepto {
  ponderados: string | null; // null si hay un multiplicador inválido
  coefAltura: string | null; // null si el coef. altura del piso es inválido
  homologados: string | null;
  porcentaje: string | null; // null si está fuera de venta o falta algún dato
  precioSugerido: string | null; // USD; null sin precio base, fuera de venta o si falta algún dato
}

/** Una cochera, baulera, local u oficina: superficie × multiplicador son sus m² ponderados y homologados. */
export interface CalculoOtra {
  homologados: string | null; // null si el multiplicador es inválido
  porcentaje: string | null; // null si falta algún dato de los deptos
  precioSugerido: string | null;
}

export interface CalculoPestana {
  departamentos: Record<number, CalculoDepto>;
  otras: Record<number, CalculoOtra>;
  totalPonderados: string | null; // deptos y cocheras y demás no fuera de venta
  totalHomologadosDeptos: string | null; // deptos no fuera de venta
  totalHomologadosOtras: string | null; // cocheras y demás no fuera de venta
  totalHomologados: string | null; // deptos no fuera de venta + otras unidades (el 100 % del fideicomiso)
  recaudacion: string | null; // Σ precio sugerido de las no fuera de venta; null sin precio base o si falta algún dato
}

/**
 * Cálculo en vivo de toda la pestaña. Las cocheras y demás: superficie total × su multiplicador (sin coef. altura),
 * ponderados = homologados. Los fuera de venta no tienen % ni suman. El reparto va ordenado por id, como el backend,
 * para que los centésimos caigan en el mismo lugar.
 */
export function calcularPestana(f: FormPestana): CalculoPestana {
  const base = precioBase(f.precio_base) ?? null;
  const departamentos: Record<number, CalculoDepto> = {};
  const otras: Record<number, CalculoOtra> = {};
  const homologados = new Map<number, bigint | null>();
  const sumar = (a: bigint | null, v: bigint | null) => (a === null || v === null ? null : a + v);
  let totalP: bigint | null = 0n;
  let totalDeptos: bigint | null = 0n;
  let totalOtras: bigint | null = 0n;
  let miles: bigint | null = base === null ? null : 0n; // la recaudación, en miles de USD (cada precio va redondeado a USD 1.000)
  const sumarPrecio = (h: bigint | null) => {
    const precio = h === null ? null : precioSugerido(h, base);
    miles = miles === null || h === null ? null : miles + (precio === null ? 0n : BigInt(precio.slice(0, -3)) / 1000n);
  };
  for (const d of f.departamentos) {
    const p = ponderadosC(d);
    const altura = positivo(d.coef_altura);
    const h = p === null ? null : homologadosC(p, altura);
    departamentos[d.id] = {
      ponderados: p === null ? null : deCentesimos(p),
      coefAltura: altura,
      homologados: h === null ? null : deCentesimos(h),
      porcentaje: null,
      precioSugerido: h === null || d.fuera_de_venta ? null : precioSugerido(h, base),
    };
    if (d.fuera_de_venta) continue;
    homologados.set(d.id, h);
    sumarPrecio(h);
    totalP = sumar(totalP, p);
    totalDeptos = sumar(totalDeptos, h);
  }
  for (const o of f.otras) {
    const mult = positivo(o.multiplicador);
    const m2 = mult === null ? null : dividirParAlMedio(aEntero(o.m2, 100n) * aEntero(mult, 1000n), 1000n);
    otras[o.id] = {
      homologados: m2 === null ? null : deCentesimos(m2),
      porcentaje: null,
      precioSugerido: m2 === null || o.fuera_de_venta ? null : precioSugerido(m2, base),
    };
    if (o.fuera_de_venta) continue;
    homologados.set(o.id, m2);
    sumarPrecio(m2);
    totalP = sumar(totalP, m2);
    totalOtras = sumar(totalOtras, m2);
  }
  const valores = [...homologados.entries()].sort(([a], [b]) => a - b);
  const completos = valores.every(([, v]) => v !== null);
  if (completos) {
    const pcts = repartirPorcentajes(valores.map(([, v]) => v as bigint));
    valores.forEach(([id], i) => {
      const destino = departamentos[id] ?? otras[id];
      if (destino) destino.porcentaje = deCentesimos(pcts[i]!);
    });
  }
  const texto = (v: bigint | null) => (v === null ? null : deCentesimos(v));
  return {
    departamentos,
    otras,
    totalPonderados: texto(totalP),
    totalHomologadosDeptos: texto(totalDeptos),
    totalHomologadosOtras: texto(totalOtras),
    totalHomologados: texto(sumar(totalDeptos, totalOtras)),
    recaudacion: miles === null ? null : `${(miles as bigint) * 1000n}.00`,
  };
}

/** El coef. altura del depto está en el estándar de su piso (si no, se marca como propio). */
export const enEstandar = (d: FormDepto): boolean => positivo(d.coef_altura) === parseCoeficiente(d.estandar);

// ------------------------------------------------------------------ por defecto (Configuración)

export type CampoPorDefecto = CampoMultiplicador | "coef_altura_base" | "coef_altura_paso";
export type FormPorDefecto = Record<CampoPorDefecto, string>;
export type ErroresPorDefecto = Partial<Record<CampoPorDefecto, string>>;

export function formPorDefecto(p: Pick<Ponderacion, CampoPorDefecto>): FormPorDefecto {
  return {
    coef_cubierta: coeficienteAInput(p.coef_cubierta),
    coef_semicubierta: coeficienteAInput(p.coef_semicubierta),
    coef_descubierta: coeficienteAInput(p.coef_descubierta),
    coef_altura_base: coeficienteAInput(p.coef_altura_base),
    coef_altura_paso: coeficienteAInput(p.coef_altura_paso),
  };
}

export function validarPorDefecto(f: FormPorDefecto): ErroresPorDefecto {
  const e: ErroresPorDefecto = {};
  for (const campo of MULTIPLICADORES) if (parseCoeficiente(f[campo]) === null) e[campo] = `${MSJ_MULTIPLICADOR} (ej. 0,5)`;
  if (positivo(f.coef_altura_base) === null) e.coef_altura_base = `${MSJ_POSITIVO} (ej. 0,92)`;
  if (parseCoeficiente(f.coef_altura_paso) === null) e.coef_altura_paso = `${MSJ_MULTIPLICADOR} (ej. 0,03)`;
  return e;
}

export function aPayloadPorDefecto(f: FormPorDefecto): Record<CampoPorDefecto, string> {
  return {
    coef_cubierta: parseCoeficiente(f.coef_cubierta) ?? "0.000",
    coef_semicubierta: parseCoeficiente(f.coef_semicubierta) ?? "0.000",
    coef_descubierta: parseCoeficiente(f.coef_descubierta) ?? "0.000",
    coef_altura_base: positivo(f.coef_altura_base) ?? "0.920",
    coef_altura_paso: parseCoeficiente(f.coef_altura_paso) ?? "0.030",
  };
}

/**
 * Coef. altura estándar de un piso, como `reglas.coef_altura_estandar`: base + paso × (piso − 1), entre 0,001 y 9,999.
 * Con milésimos enteros; null si la base o el paso son inválidos.
 */
export function alturaEstandar(piso: number, base: string, paso: string): string | null {
  const b = positivo(base);
  const s = parseCoeficiente(paso);
  if (b === null || s === null) return null;
  let m = aEntero(b, 1000n) + aEntero(s, 1000n) * BigInt(piso - 1);
  if (m < 1n) m = 1n;
  if (m > 9999n) m = 9999n;
  return `${m / 1000n}.${String(m % 1000n).padStart(3, "0")}`;
}

/** Vista previa de la escala en Configuración: "1° 0,92 · 2° 0,95 · …" (los pisos con deptos o, sin deptos, del 1° al 7°). */
export function escalaAltura(f: FormPorDefecto, pisos: number[]): string | null {
  const lista = pisos.length > 0 ? pisos : [1, 2, 3, 4, 5, 6, 7];
  const partes: string[] = [];
  for (const p of lista) {
    const c = alturaEstandar(p, f.coef_altura_base, f.coef_altura_paso);
    if (c === null) return null;
    partes.push(`${etiquetaPiso(p)} ${coeficienteAInput(c)}`);
  }
  return partes.join(" · ");
}

// ------------------------------------------------------------------ resumen (tabla de abajo, siempre a la vista)

export interface FilaResumen {
  id: number;
  nombre: string;
  depto: boolean;
  fuera_de_venta: boolean;
  m2Propios: string; // cubierta + semicubierta reales (cocheras y demás: su superficie total)
  m2Descubiertos: string | null; // reales; null en cocheras y demás
  ponderados: string | null;
  ratio: string | null; // el coef. altura del depto; null en cocheras y demás (no lo llevan)
  homologados: string | null;
  porcentaje: string | null;
  valor: string | null; // precio sugerido (USD)
}

/** Una fila por depto y por cochera y demás, con los m² reales y lo calculado (Mati, 2026-10-10). */
export function filasResumen(f: FormPestana, c: CalculoPestana): FilaResumen[] {
  const deptos = f.departamentos.map((d): FilaResumen => {
    const x = c.departamentos[d.id];
    return {
      id: d.id, nombre: d.nombre, depto: true, fuera_de_venta: d.fuera_de_venta,
      m2Propios: deCentesimos(aEntero(d.sup_cubierta_m2, 100n) + aEntero(d.sup_semicubierta_m2, 100n)),
      m2Descubiertos: d.sup_descubierta_m2,
      ponderados: x?.ponderados ?? null, ratio: x?.coefAltura ?? null, homologados: x?.homologados ?? null,
      porcentaje: x?.porcentaje ?? null, valor: x?.precioSugerido ?? null,
    };
  });
  const otras = f.otras.map((o): FilaResumen => {
    const x = c.otras[o.id];
    return {
      id: o.id, nombre: o.nombre, depto: false, fuera_de_venta: o.fuera_de_venta, m2Propios: o.m2, m2Descubiertos: null,
      ponderados: x?.homologados ?? null, ratio: null, homologados: x?.homologados ?? null,
      porcentaje: x?.porcentaje ?? null, valor: x?.precioSugerido ?? null,
    };
  });
  return [...deptos, ...otras];
}
